import {
  normalizeWidgetTheme,
  APIURL,
  cancelSelectEvent,
  changeSymbolForWidget,
  getCSSStyles,
  getEChartsTooltipTheme,
  getSymbolDropdown,
  getTraderSMCardHTML,
  getWidgetDataURI,
  apiGet,
  toggleWidgetTheme,
  getHeaderPinStateClass,
  getHeaderPinButtonHTML,
  setupHeaderPin
} from '../../helpers';
import { WidgetConfig, WidgetEventHandler, WidgetEventType, WidgetEvent } from '../../types';

interface QuarterlyTradingRecord {
  year: number;
  quarter: number;
  time_period: string;
  acquired_transactions: number;
  disposed_transactions: number;
}

interface QuarterlyTradingChartData {
  time_periods: string[];
  acquired_transactions: number[];
  disposed_transactions: number[];
}

interface QuarterlyTradingApiData {
  symbol: string;
  total_periods: number;
  summary: {
    total_acquired_transactions: number;
    total_disposed_transactions: number;
    avg_acquired_per_quarter: number;
    avg_disposed_per_quarter: number;
    max_acquired: number;
    max_disposed: number;
    first_year: number;
    last_year: number;
  };
  chart_data: QuarterlyTradingChartData;
  data: QuarterlyTradingRecord[];
}

interface QuarterlyTradingConfig extends WidgetConfig {}

export class TraderverseQuarterlyTradingBasedonAcquisitionandDisposition {
  private container: HTMLElement;
  private config: QuarterlyTradingConfig;
  private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
  private shadowRoot: ShadowRoot;
  private chartInstance: any = null;
  private apiData: QuarterlyTradingApiData | null = null;
  private widgetResizeObserver: ResizeObserver | null = null;
  private hiddenSeries: Set<string> = new Set();
  private filters = {
    symbol: '' as string | undefined
  };

  constructor(config: QuarterlyTradingConfig) {
    this.config = { ...this.getDefaultConfig(), ...config };
    this.config.theme = normalizeWidgetTheme(this.config.theme);
    this.applyConfigFilters();
    this.container = this.resolveContainer(config.container);

    if ((this.container as any).shadowRoot) {
      this.shadowRoot = (this.container as any).shadowRoot;
    } else {
      this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
    }

    this.init();

    this.widgetResizeObserver = new ResizeObserver(() => this.resize());
    setTimeout(() => {
      const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
      if (widget) this.widgetResizeObserver?.observe(widget);
    }, 0);
  }

  private getDefaultConfig(): Partial<QuarterlyTradingConfig> {
    return {
      headerPinned: true,
      theme: 'white',
      width: '100%',
      height: '400px',
      autosize: true,
      isSymbolSelectable: true,
      symbol: 'AAPL'
    };
  }

  private applyConfigFilters(): void {
    if (this.config.symbol) this.filters.symbol = this.config.symbol;
  }

  private resolveContainer(container: string | HTMLElement): HTMLElement {
    if (typeof container === 'string') {
      const element = document.getElementById(container) || document.querySelector(container);
      if (!element) {
        throw new Error(`Container not found: ${container}`);
      }
      return element as HTMLElement;
    }
    return container;
  }

  private async init(): Promise<void> {
    this.createWidget();
    await this.attachStyles();
    await this.injectChartLibrary();

    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    if (widget) widget.classList.add('loading');
    await this.setupChart();
    if (widget) widget.classList.remove('loading');

    cancelSelectEvent(this.shadowRoot);
    this.emit('ready', {});
    this.config.onReady?.();
  }

  private createWidget(): void {
    let themeClass = 'defaultDarkMode';
    let dataTheme = 'default-dark';

    if (this.config.theme === 'white') {
      themeClass = 'whiteMode';
      dataTheme = 'white';
    } else if (this.config.theme === 'black') {
      themeClass = 'blackMode';
      dataTheme = 'black';
    } else if (this.config.theme === 'default-lite') {
      themeClass = 'defaultLightMode';
      dataTheme = 'default-lite';
    }

    const widgetHtml = `
      <div style="width: ${typeof this.config.width == 'number' ? this.config.width + 'px' : this.config.width};height: ${typeof this.config.height == 'number' ? this.config.height + 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
          <div class="headTop">
            <h4 class="title market">${this.getTitleIconSvg()} Quarterly Trading Based on Acquisition and Disposition</h4>
            <div class="actionCol">${getHeaderPinButtonHTML(this.config)}
              <i class="pi pi-ellipsis-v"></i>
            </div>
          </div>
          <div class="inBody">
            <div class="optionsRow">
                ${this.config.hideSymbol ? '<div class="item dropStyle01"></div>' : `<div class="item dropStyle01">
                <div class="trader-sm-card" id="selected-symbol">
                ${getTraderSMCardHTML(this.config.stock ?? { symbol: this.config.symbol!, type: 'stock', name: '' })}
                </div>
                <span id="symbol-dropdown-placeholder"></span>
                </div>`}
                <span></span>
              <div class="item">
                <div class="legendsCollection">
                  <span class="toggle-series active" data-series-index="0" style="background-color:#8f5efc;" data-testing-id="legend-series-0" data-series-name="Acquisitions"></span>
                  <span class="toggle-series active" data-series-index="1" style="background-color:#fdb52a;" data-testing-id="legend-series-1" data-series-name="Dispositions"></span>
                </div>
              </div>
            </div>
            <div class="chartDv" id="quarterlyTradingChartDv" style="min-height:320px;">
              <div id="chartContainer" class="chart coverDv" style="width:100%;height:100%;min-height:300px;"></div>
            </div>
          </div>
        </div>
    `;

    this.shadowRoot.innerHTML = widgetHtml;
    if (this.config.isSymbolSelectable && !this.config.hideSymbol) {
      const dropdown = getSymbolDropdown((data: any) => this.onSelectChange(data));
      const placeholder = this.shadowRoot.getElementById('symbol-dropdown-placeholder');
      if (placeholder && dropdown) {
        placeholder.replaceWith(dropdown);
      }
    }
    this.setupLegendToggles();
    this.setupEventListeners();
  }

  private setupLegendToggles(): void {
    const legendSpans = this.shadowRoot.querySelectorAll('.toggle-series');
    legendSpans.forEach((span) => {
      span.addEventListener('click', (e) => {
        const target = e.target as HTMLElement;
        const seriesIndex = parseInt(target.getAttribute('data-series-index') || '0');
        target.classList.toggle('active');
        this.toggleSeries(seriesIndex, target.classList.contains('active'));
      });
    });
  }

  private toggleSeries(seriesIndex: number, isVisible: boolean): void {
    if (!this.chartInstance) return;

    const option = this.chartInstance.getOption();
    if (!option?.series?.[seriesIndex]) return;

    const newOption = {
      ...option,
      series: option.series.map((series: any, index: number) => {
        if (index === seriesIndex) {
          return {
            ...series,
            lineStyle: { ...series.lineStyle, opacity: isVisible ? 1 : 0 },
            areaStyle: { ...series.areaStyle, opacity: isVisible ? 0.55 : 0 }
          };
        }
        return series;
      })
    };

    this.chartInstance.setOption(newOption);

    const name = option.series[seriesIndex]?.name;
    if (name) {
      if (isVisible) this.hiddenSeries.delete(name);
      else this.hiddenSeries.add(name);
    }
  }

  private setupEventListeners(): void {
    setupHeaderPin(this.shadowRoot, this.config);
  }

  private onSelectChange(stock: any): void {
    this.changeSymbol(stock.symbol, stock);
  }

  private buildQueryParams(): Record<string, string | number> {
    const query: Record<string, string | number> = {};
    if (this.filters.symbol) query.symbol = this.filters.symbol;
    return query;
  }

  private mapChartData(data: QuarterlyTradingApiData | null | undefined): QuarterlyTradingChartData | null {
    if (!data) return null;

    const chartData = data.chart_data;
    if (chartData?.time_periods?.length) {
      return {
        time_periods: chartData.time_periods,
        acquired_transactions: chartData.acquired_transactions ?? [],
        disposed_transactions: chartData.disposed_transactions ?? []
      };
    }

    const records = data.data;
    if (!records?.length) return null;

    return {
      time_periods: records.map((row) => row.time_period),
      acquired_transactions: records.map((row) => row.acquired_transactions ?? 0),
      disposed_transactions: records.map((row) => row.disposed_transactions ?? 0)
    };
  }

  private hasChartData(data: QuarterlyTradingApiData | null): boolean {
    const chartData = this.mapChartData(data);
    if (!chartData?.time_periods?.length) return false;
    return chartData.acquired_transactions.some((v) => v > 0)
      || chartData.disposed_transactions.some((v) => v > 0);
  }

  private async attachStyles(): Promise<void> {
    try {
      const styleEl = await getCSSStyles();
      this.shadowRoot.appendChild(styleEl);
    } catch (err) {
      console.error('Failed to load CSS file:', err);
    }
  }

  private async injectChartLibrary(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      if ((window as any).echarts) return resolve(true);
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js';
      script.async = true;
      script.onload = () => resolve(true);
      this.shadowRoot.appendChild(script);
    });
  }

  private async setupChart(symbol?: string): Promise<void> {
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    const chartWrapper = this.shadowRoot.querySelector('#quarterlyTradingChartDv') as HTMLElement;
    if (widget) widget.classList.add('loading');

    if (symbol) {
      this.filters.symbol = symbol;
      this.config.symbol = symbol;
    }

    try {
      const response = await apiGet({
        method: 'GET',
        url: `${APIURL}/v1/equity-market/quarterly-trading-acquisition-disposition`,
        query: this.buildQueryParams(),
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const json = await response.json();
      this.apiData = json?.data as QuarterlyTradingApiData | null;

      if (this.hasChartData(this.apiData)) {
        chartWrapper?.setAttribute('data-testing-id', 'quarterly-trading-acquisition-disposition');
      } else {
        chartWrapper?.removeAttribute('data-testing-id');
      }

      this.updateDisplay();
      this.emit('dataUpdate', this.apiData);
    } catch (error) {
      console.error('Error loading quarterly trading data:', error);
      this.apiData = null;
      chartWrapper?.removeAttribute('data-testing-id');
      this.updateDisplay();
      this.emit('error', error);
    } finally {
      if (widget) widget.classList.remove('loading');
    }
  }

  private updateDisplay(): void {
    if (!(window as any).echarts) {
      setTimeout(() => this.updateDisplay(), 100);
      return;
    }
    this.drawChart();
  }

  private drawChart(): void {
    const wrapper = this.shadowRoot.querySelector('#quarterlyTradingChartDv') as HTMLElement;
    const container = this.shadowRoot.querySelector('#chartContainer') as HTMLElement;
    if (!container) return;

    const echarts = (window as any).echarts;
    const chartData = this.mapChartData(this.apiData);

    if (!chartData?.time_periods?.length) {
      if (this.chartInstance) {
        this.chartInstance.dispose();
        this.chartInstance = null;
      }
      container.innerHTML = '<div class="noData"><p>No data available</p></div>';
      wrapper?.removeAttribute('data-testing-id');
      return;
    }

    const existing = echarts.getInstanceByDom(container);
    if (existing) echarts.dispose(existing);
    container.innerHTML = '';
    wrapper?.setAttribute('data-testing-id', 'quarterly-trading-acquisition-disposition');

    const timePeriods = chartData.time_periods;
    const acquired = chartData.acquired_transactions;
    const disposed = chartData.disposed_transactions;

    const isAcqHidden = this.hiddenSeries.has('Acquisitions');
    const isDispHidden = this.hiddenSeries.has('Dispositions');

    const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    const textColor = getComputedStyle(widgetBox).getPropertyValue('--charts-text-color').trim() || '#736d9d';
    const axisLineColor = getComputedStyle(widgetBox).getPropertyValue('--charts-axis-color').trim() || '#302c57';
    const splitLineColor = getComputedStyle(widgetBox).getPropertyValue('--charts-split-line-color').trim() || 'rgba(115, 109, 157, 0.15)';
    const fontSize = getComputedStyle(widgetBox).getPropertyValue('--font-size-sm').trim() || '12px';

    const defaultVisibleCount = 20;
    const startPercent = timePeriods.length > defaultVisibleCount
      ? ((timePeriods.length - defaultVisibleCount) / timePeriods.length) * 100
      : 0;

    const option = {
      backgroundColor: 'transparent',
      legend: { show: false },
      grid: { left: 16, right: 16, top: 20, bottom: 48, containLabel: true },
      dataZoom: [
        { type: 'inside', start: startPercent, end: 100 },
        {
          type: 'slider',
          start: startPercent,
          end: 100,
          height: 18,
          bottom: 8,
          borderColor: axisLineColor,
          textStyle: { color: textColor, fontSize }
        }
      ],
      xAxis: {
        type: 'category',
        data: timePeriods,
        axisLine: { lineStyle: { color: axisLineColor } },
        axisLabel: {
          color: textColor,
          fontSize,
          rotate: 0,
          interval: Math.max(0, Math.floor(timePeriods.length / 12))
        },
        axisTick: { show: false }
      },
      yAxis: {
        type: 'value',
        axisLine: { show: false },
        axisLabel: { color: textColor, fontSize },
        axisTick: { show: false },
        splitLine: {
          lineStyle: { color: splitLineColor, type: 'dashed' }
        }
      },
      series: [
        {
          name: 'Acquisitions',
          type: 'line',
          data: acquired,
          smooth: true,
          symbol: 'none',
          lineStyle: { color: '#8f5efc', width: 0.5, opacity: isAcqHidden ? 0 : 1 },
          areaStyle: {
            opacity: isAcqHidden ? 0 : 0.55,
            color: {
              type: 'linear',
              x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(143, 94, 252, 0.55)' },
                { offset: 1, color: 'rgba(143, 94, 252, 0.05)' }
              ]
            }
          }
        },
        {
          name: 'Dispositions',
          type: 'line',
          data: disposed,
          smooth: true,
          symbol: 'none',
          lineStyle: { color: '#fdb52a', width: 0.5, opacity: isDispHidden ? 0 : 1 },
          areaStyle: {
            opacity: isDispHidden ? 0 : 0.55,
            color: {
              type: 'linear',
              x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(253, 181, 42, 0.55)' },
                { offset: 1, color: 'rgba(253, 181, 42, 0.05)' }
              ]
            }
          }
        }
      ],
      tooltip: {
        trigger: 'axis',
        ...getEChartsTooltipTheme(widgetBox),
        formatter: (params: any) => {
          const period = params[0]?.name ?? '';
          const index = params[0]?.dataIndex ?? 0;
          const record = this.apiData?.data?.[index];
          let tooltip = `<b>${period}</b>`;
          if (record) tooltip += ` (${record.year} Q${record.quarter})`;
          tooltip += '<br/>';
          params.forEach((item: any) => {
            tooltip += `${item.marker} <b>${item.seriesName}</b>: ${item.value ?? 0}<br/>`;
          });
          return tooltip;
        }
      }
    };

    this.chartInstance = echarts.init(container);
    this.chartInstance.setOption(option);
    setTimeout(() => this.chartInstance?.resize(), 100);
  }

  private changeSymbol(stockId: string, stock: any = null): void {
    changeSymbolForWidget(
      this.shadowRoot,
      this.config,
      stock,
      stockId,
      (symbol: string) => this.setupChart(symbol),
      (type: any, data: any) => this.emit(type, data)
    );
  }

  public on(eventType: WidgetEventType, handler: WidgetEventHandler): void {
    if (!this.eventHandlers.has(eventType)) {
      this.eventHandlers.set(eventType, []);
    }
    this.eventHandlers.get(eventType)!.push(handler);
  }

  public off(eventType: WidgetEventType, handler: WidgetEventHandler): void {
    const handlers = this.eventHandlers.get(eventType);
    if (handlers) {
      const index = handlers.indexOf(handler);
      if (index > -1) handlers.splice(index, 1);
    }
  }

  public async getWidgetImage(): Promise<string> {
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    return await getWidgetDataURI(widget);
  }

  public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
    if (!this.shadowRoot) return;

    this.config.theme = toggleWidgetTheme(
      this.shadowRoot,
      theme
    ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';

    this.updateDisplay();
  }

  public setSymbol(symbol: string): void {
    this.changeSymbol(symbol);
  }

  public refresh(): void {
    this.setupChart();
  }

  public resize(): void {
    if (this.chartInstance) {
      this.chartInstance.resize();
    }
  }

  public destroy(): void {
    if (this.chartInstance) {
      this.chartInstance.dispose();
      this.chartInstance = null;
    }
    if (this.widgetResizeObserver) {
      this.widgetResizeObserver.disconnect();
      this.widgetResizeObserver = null;
    }
    this.eventHandlers.clear();
    this.shadowRoot.innerHTML = '';
  }

  private emit(type: WidgetEventType, data: any): void {
    const handlers = this.eventHandlers.get(type);
    if (handlers) {
      const event: WidgetEvent = { type, data };
      handlers.forEach(handler => handler(event));
    }
  }

  private getTitleIconSvg(): string {
    return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> <path d="M9.16252 19.05C8.96252 19.05 8.76252 18.9875 8.58752 18.85C8.17502 18.5375 8.10002 17.95 8.41252 17.5375L11.3875 13.675C11.75 13.2125 12.2625 12.9125 12.85 12.8375C13.425 12.7625 14.0125 12.925 14.475 13.2875L16.7625 15.0875C16.85 15.1625 16.9375 15.1625 17 15.15C17.05 15.15 17.1375 15.125 17.2125 15.025L20.1 11.3C20.4125 10.8875 21.0125 10.8125 21.4125 11.1375C21.825 11.45 21.9 12.0375 21.575 12.45L18.6875 16.175C18.325 16.6375 17.8125 16.9375 17.225 17C16.6375 17.075 16.0625 16.9125 15.6 16.55L13.3125 14.75C13.225 14.675 13.125 14.675 13.075 14.6875C13.025 14.6875 12.9375 14.7125 12.8625 14.8125L9.88752 18.675C9.72502 18.925 9.45002 19.05 9.16252 19.05Z" fill="currentColor"/> </svg>`;
  }
}

declare global {
  interface Window {
    TraderverseQuarterlyTradingBasedonAcquisitionandDisposition: typeof TraderverseQuarterlyTradingBasedonAcquisitionandDisposition;
  }
}

if (typeof window !== 'undefined') {
  window.TraderverseQuarterlyTradingBasedonAcquisitionandDisposition = TraderverseQuarterlyTradingBasedonAcquisitionandDisposition;
}
